import { Add, Delete, DoNotDisturb, Edit, Menu } from '@mui/icons-material';
import { Box, GridLegacy as Grid, IconButton } from '@mui/material';
import _ from 'lodash';
import React, { useContext, useState } from 'react';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { DisableContext } from '../../../contexts/DisableContextProvider';
import { Step } from '../../../types/Step';
import { UserSettingsCollapseKeys } from '../../../types/User';
import AddStepModal from './AddStepModal';
import ResponsiveButton from '../../../uikit/buttons/ResponsiveButton';
import AccordionSection from '../../AccordionSection';

interface StepsListProps {
  steps: Step[];
  setSteps: (steps: Step[]) => void;
}

export default function StepsList({ steps, setSteps }: StepsListProps) {
  const [addStepModalOpen, setAddStepModalOpen] = useState<boolean>(false);
  const [editingStep, setEditingStep] = useState<Step | null>(null);
  const { disabled } = useContext(DisableContext);

  const updateStepOrder = (newSteps: Step[]) => {
    const updatedSteps = _.map(newSteps, (step, index) => ({ ...step, order: index }));
    setSteps(updatedSteps);
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const sortedSteps = _.orderBy(steps, ['order']);
    const reorderedSteps = _.cloneDeep(sortedSteps);
    const [removed] = reorderedSteps.splice(result.source.index, 1);
    reorderedSteps.splice(result.destination.index, 0, removed);
    updateStepOrder(reorderedSteps);
  };

  const handleEditStep = (step: Step) => {
    setEditingStep(step);
    setAddStepModalOpen(true);
  };

  const handleDeleteStep = (uuid: string) => {
    const newSteps = _.reject(steps, { uuid });
    updateStepOrder(newSteps);
  };

  const handleModalSubmit = (inputValue: string) => {
    if (!inputValue.trim()) return;

    if (editingStep) {
      const updatedSteps = _.map(steps, (step) => (step.uuid === editingStep.uuid ? { ...step, name: inputValue } : step));
      setSteps(updatedSteps);
    } else {
      const newStep: Step = {
        uuid: crypto.randomUUID(),
        name: inputValue,
        order: steps.length,
      };
      setSteps(_.concat(steps, newStep));
    }

    setAddStepModalOpen(false);
    setEditingStep(null);
  };

  return (
    <AccordionSection
      title="Steps"
      headerElements={
        disabled ? null : (
          <ResponsiveButton variant="outlined" startIcon={<Add />} onClick={() => setAddStepModalOpen(true)}>
            Add
          </ResponsiveButton>
        )
      }
      collapseId={UserSettingsCollapseKeys.TASK_ITEM_STEPS}
    >
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="steps-droppable">
          {(provided) => (
            <Grid container spacing={1} ref={provided.innerRef} {...provided.droppableProps}>
              {_.map(_.orderBy(steps, ['order'], ['asc']), (step, index) => (
                <Draggable key={step.uuid} draggableId={step.uuid} index={index} isDragDisabled={!!disabled}>
                  {(provided) => (
                    <Grid
                      container
                      item
                      xs={12}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      sx={{
                        alignItems: 'center',
                        border: '1px solid #ccc',
                        borderRadius: 1,
                        p: 1,
                        my: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          px: 1,
                        }}
                      >
                        {disabled ? <DoNotDisturb fontSize="small" /> : <Menu fontSize="small" />}
                      </Box>
                      <Box flex={1}>{step.name}</Box>
                      {!disabled && (
                        <Box display="flex" gap={1}>
                          <IconButton onClick={() => handleEditStep(step)} size="small">
                            <Edit fontSize="small" />
                          </IconButton>
                          <IconButton onClick={() => handleDeleteStep(step.uuid)} size="small">
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      )}
                    </Grid>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Grid>
          )}
        </Droppable>
      </DragDropContext>

      <AddStepModal
        open={addStepModalOpen}
        initialValue={editingStep ? editingStep.name : ''}
        onClose={() => {
          setAddStepModalOpen(false);
          setEditingStep(null);
        }}
        onSubmit={handleModalSubmit}
      />
    </AccordionSection>
  );
}
